/**
 * Pure transforms between the catalog-operations / enrichments wire format
 * and the frontend's UI types. Keeping these pure (no Ky calls, no React
 * hooks) means the service module and any future MSW handlers stay in
 * lock-step with the backend contract.
 *
 * Several backend fields don't yet have a UI counterpart (or vice-versa);
 * those gaps are documented inline so the next contract iteration can close
 * them in one pass.
 */

import type { EnrichmentRunConfig } from '~/features/content-enrichment/types/enrichment-run-config';
import {
  occasionCoverageToComparison,
  tagCoverageToAttributes,
  trendCoverageToComparison,
} from '~/utils/coverage-transforms';
import { formatDateTimeShort, formatProcessingDuration } from '~/utils/date';
import { metaCategoryAttributeEntries } from '~/utils/meta-category-attributes';
import { formatProductHighlights } from '~/utils/product-highlights';
import { toSourceShippingAttributes } from '~/utils/source-shipping';

import type {
  AttributeCoverageWire,
  BeforeAfterCountWire,
  BatchDeliveryHealthWire,
  BatchDetailResponse,
  BatchEnrichmentCoverageWire,
  BatchOptimizationQualityWire,
  BatchListItemWire,
  BatchListResponse,
  BatchPipelineStatusWire,
  BatchStageWire,
  BatchStatusWire,
  CatalogStatsResponse,
  CategoryCoverageWire,
  ConnectorCodeWire,
  EnrichmentJobCoverageResponse,
  EnrichmentJobHeaderResponse,
  LiveStageResponse,
  GapAnalysisProductWire,
  GapAnalysisSummaryWire,
  GapAnalysisSectionWire,
  GapAttributeWire,
  JobStatusCountsResponse,
  PerformanceKpisWire,
  OnDemandScoreResponseWire,
  OnDemandScoreSideWire,
  PhraseDescriptionWire,
  ProductAttributeEditWire,
  ProductEnrichmentFieldWire,
  ProductEnrichmentResponse,
  ProductSearchDescriptorsWire,
  SearchPivotWire,
} from '../types/catalog-operations.api.types';
import type {
  AttentionCategory,
  AttentionColor,
  AttributeCoverageRow,
  BatchAttentionItem,
  BatchAttributeRow,
  BatchCoverageDetail,
  BatchDetail,
  BatchHeader,
  BatchLiveStage,
  BatchListPage,
  BatchPipelineStage,
  BatchPipelineStageKey,
  BatchProcessingColumn,
  BatchRecord,
  BatchStatus,
  CatalogStats,
  CategoryCoverageRow,
  CoverageStat,
  CoverageTone,
  EnrichmentCoverage,
  GapAnalysisProduct,
  GapAnalysisSummary,
  GapAnalysisSection,
  GapAttribute,
  JobStatusCounts,
  OnDemandScoreResult,
  OnDemandScoreSide,
  OptimizationContentType,
  OptimizationQuality,
  PerformanceKpis,
  PerformanceSignals,
  PhraseDescription,
  ProductAttributeEdit,
  ProductDetail,
  ProductOptimization,
  ProductSearchDescriptors,
  QualityScorePanel,
  QualityScoreRow,
  SearchPivot,
} from '../types/catalog-operations.types';

// ---------------------------------------------------------------------------
// Null coercion helper
// ---------------------------------------------------------------------------

/** Coerces a nullable number to a defaulted number. The enrichments service
 *  routinely sends `null` for fields the OpenAPI marks as required while
 *  backfill is in progress; the UI invariants assume a number, so we
 *  centralise the coercion here. */
function nz(value: number | null | undefined, fallback = 0): number {
  return value ?? fallback;
}

// ---------------------------------------------------------------------------
// Status / connector coercion
// ---------------------------------------------------------------------------

/** Folds the 8-value backend status enum into the 5-value UI BatchStatus.
 *  PENDING / AWAITING_APPROVAL / RESET all surface as "in-progress" since the
 *  table doesn't expose dedicated tabs for those states yet. Null falls
 *  through to "in-progress" so a partially-backfilled row still renders.
 *  RUNNING is the experiment-domain equivalent of IN_PROGRESS — it only
 *  appears when this job was looked up by experiment id — and folds the same
 *  way; `BatchDetailHeader` re-labels it "Running" for that flow. ARCHIVED
 *  surfaces as "cancelled" — set by the enrichment-jobs archive endpoint used
 *  by the zero-eligible-products restart guard.
 *  NOTE: 'needs-attention' is not yet emitted here because BatchStatusWire has
 *  no NEEDS_ATTENTION variant — it is reserved in BatchStatus for when the API
 *  adds it, at which point this switch should gain an explicit case for it. */
export function toBatchStatus(wire: BatchStatusWire | null): BatchStatus {
  switch (wire) {
    case 'COMPLETED':
      return 'delivered';
    case 'FAILED':
      return 'failed';
    case 'AWAITING_APPROVAL':
      return 'awaiting-approval';
    case 'REJECTED':
      return 'rejected';
    case 'RUNNING':
      return 'running';
    case 'ARCHIVED':
      return 'cancelled';
    case 'IN_PROGRESS':
    case 'PENDING':
    case 'RESET':
    default:
      return 'in-progress';
  }
}

/** Overlays the decoupled-delivery sub-state onto the display status. A run
 *  reads `DELIVERING`/`REJECTED` on `pipelineStatus` while its top-level
 *  `status` is still `AWAITING_APPROVAL`, so the chip must follow the sub-state
 *  when present; otherwise it falls back to the plain status mapping. */
export function resolveDisplayStatus(
  status: BatchStatusWire | null,
  pipelineStatus: BatchPipelineStatusWire | null | undefined,
): BatchStatus {
  if (pipelineStatus === 'DELIVERING') return 'delivering';
  if (pipelineStatus === 'REJECTED' || status === 'REJECTED') return 'rejected';
  // A settled pipeline whose top-level status lagged at AWAITING_APPROVAL has
  // left the gate — show the real outcome instead of "Review". Scoped to
  // AWAITING_APPROVAL so ARCHIVED/IN_PROGRESS (re-triggered sibling) still win.
  if (status === 'AWAITING_APPROVAL') {
    if (pipelineStatus === 'COMPLETED') return 'delivered';
    if (pipelineStatus === 'FAILED') return 'failed';
  }
  return toBatchStatus(status);
}

/** Maps the four UI tabs to the backend status filter. `'in-progress'`
 *  intentionally maps to `IN_PROGRESS` only — PENDING / AWAITING_APPROVAL /
 *  RESET batches surface in the All Batches tab until the backend supports
 *  multi-status filtering. */
export function uiTabToWireStatus(tab: string): BatchStatusWire | undefined {
  switch (tab) {
    case 'in-progress':
      return 'IN_PROGRESS';
    case 'delivered':
      return 'COMPLETED';
    case 'failed':
      return 'FAILED';
    default:
      return undefined;
  }
}

/** Passthrough mapper for `/jobs/status-counts` — wire and UI shapes are
 *  identical, kept as separate types so components never import the wire
 *  type directly. */
export function toJobStatusCounts(wire: JobStatusCountsResponse): JobStatusCounts {
  return { ...wire };
}

/** Connector code → display name shown in the Source platform column. Null
 *  is rendered as an em-dash so the row still renders during backfill. */
export function toPlatformName(code: ConnectorCodeWire | null): string {
  switch (code) {
    case 'GMC':
      return 'Google';
    case 'META':
      return 'Meta';
    case 'SHOPIFY':
      return 'Shopify';
    case 'FILE':
      return 'File';
    case 'PIM':
      return 'PIM';
    case null:
      return '—';
    default:
      return code;
  }
}

/** Translates the jobs-listing's short connector enum (`GMC`, `META`, …) to
 *  the long form the curation API expects on its query string and headers
 *  (`GOOGLE_MERCHANT`, `META_COMMERCE`, …). Returns `undefined` for null so
 *  callers can keep their queries disabled until the value resolves. */
export function toCurationConnectorCode(code: ConnectorCodeWire | null): string | undefined {
  switch (code) {
    case 'GMC':
      return 'GOOGLE_MERCHANT';
    case 'META':
      return 'META_COMMERCE';
    case 'SHOPIFY':
      return 'SHOPIFY';
    case 'FILE':
      return 'FILE';
    case 'PIM':
      return 'PIM';
    case null:
      return undefined;
    default:
      return code;
  }
}

/** Use-case code → human label (e.g. "GMC_ADS" → "Google Ads"). Used for the
 *  source chip on the batch detail header. Falls back to the connector
 *  platform when the value is null so the chip is never blank. */
export function toUseCaseLabel(useCase: string | null, fallbackPlatform: string): string {
  if (!useCase) return fallbackPlatform;
  switch (useCase) {
    case 'GMC_ADS':
      return 'Google Ads';
    case 'META_ADS':
      return 'Meta Ads';
    case 'SHOPIFY_ADS':
      return 'Shopify Ads';
    default:
      // Generic transform: "GMC_ADS" → "Gmc Ads". Better than the raw enum.
      return useCase
        .toLowerCase()
        .split('_')
        .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ');
  }
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

/** Maps the /jobs/summary payload into the UI shape consumed by the listing
 *  page. The backend returns two attention buckets (critical, warning); we
 *  surface them as `AttentionCategory[]` so the alert banner renders without
 *  any caller-side branching. */
export function toCatalogStats(wire: CatalogStatsResponse): CatalogStats {
  const categories: AttentionCategory[] = [];
  const critical = wire.needsAttentionCritical ?? 0;
  const warning = wire.needsAttentionWarning ?? 0;
  if (critical > 0) {
    categories.push({ count: critical, description: 'critical issues' });
  }
  if (warning > 0) {
    categories.push({ count: warning, description: 'warnings' });
  }

  return {
    batchesThisWeek: wire.batchesThisWeek ?? 0,
    successRate: wire.batchesThisWeekSuccessRate ?? 0,
    totalProductsDelivered: wire.totalProductsDelivered ?? 0,
    // Preserve null — UI renders "—" when there is no last-week comparison.
    totalProductsDeliveredDelta: wire.totalProductsDeliveredDelta,
    avgProcessingLatencyMs: wire.avgProcessingLatencyMs ?? 0,
    avgProcessingLatencyDelta: wire.avgProcessingLatencyDelta ?? 0,
    needsAttentionTotal: wire.needsAttentionCount ?? 0,
    needsAttentionCategories: categories,
  };
}

// ---------------------------------------------------------------------------
// Batches list
// ---------------------------------------------------------------------------

/** Single-row mapper. `enrichmentType` is derived from the (now scalar)
 *  `useCase`; falls back to the connector code so the column is never blank.
 *  Defensive coercion guards every nullable wire field — backend backfill is
 *  still in progress and several "required" fields routinely arrive null. */
export function toBatchRecord(wire: BatchListItemWire): BatchRecord {
  const platform = toPlatformName(wire.connectorCode);
  const enrichmentType = wire.useCase ?? wire.connectorCode ?? '—';
  return {
    id: wire.id,
    batchId: wire.batchId ?? '—',
    name: wire.name ?? '',
    status: resolveDisplayStatus(wire.status, wire.pipelineStatus),
    createdAt: wire.createdAt ?? '',
    deliveredAt: wire.deliveredAt,
    enrichmentType,
    productCount: wire.productsCount ?? 0,
    sourcePlatform: platform,
    destinationPlatform: platform,
    coveragePercent: nz(wire.optimizationCoverage),
    source: wire.source ?? '',
    trialStage: wire.trialStage ?? null,
    trialId: wire.trialId ?? null,
    pipelineStatus: wire.pipelineStatus ?? null,
    workflowId: wire.workflowId ?? null,
    connectorCode: wire.connectorCode ?? null,
    archivedAt: wire.archivedAt ?? null,
    archivedBy: wire.archivedBy ?? null,
    createdBy: wire.createdBy ?? null,
    lastActionBy: wire.lastActionBy ?? null,
    lastActionAt: wire.lastActionAt ?? null,
    lastActionType: wire.lastActionType ?? null,
    costGate: wire.costGate ?? null,
    // Batch pause / retry — conditionally spread so absent stays undefined.
    ...(wire.batchStatus != null ? { batchStatus: wire.batchStatus } : {}),
    ...(wire.retryAttempts != null ? { retryAttempts: wire.retryAttempts } : {}),
    ...(wire.activeRetryRunId != null ? { activeRetryRunId: wire.activeRetryRunId } : {}),
    ...(wire.pausedAt != null ? { pausedAt: wire.pausedAt } : {}),
    ...(wire.pausedBy != null ? { pausedBy: wire.pausedBy } : {}),
    ...(wire.needsAttention != null ? { needsAttention: wire.needsAttention } : {}),
    ...(wire.productsExcluded != null ? { productsExcluded: wire.productsExcluded } : {}),
  };
}

/** Maps the Spring-page envelope into a UI-friendly pagination shape. */
export function toBatchListPage(wire: BatchListResponse): BatchListPage {
  return {
    items: wire.content.map(toBatchRecord),
    page: wire.page,
    pageSize: wire.size,
    total: wire.totalElements,
    totalPages: wire.totalPages,
  };
}

// ---------------------------------------------------------------------------
// Batch detail
// ---------------------------------------------------------------------------

/** Backend stage names are free-form strings (e.g. "Received", "Enriched");
 *  the UI uses a fixed key set for icons and color logic. Names that don't
 *  match a known key fall back to "received" rather than crashing the page. */
function toPipelineStageKey(name: string): BatchPipelineStageKey {
  const key = name.trim().toLowerCase();
  if (
    key === 'received' ||
    key === 'curated' ||
    key === 'enriched' ||
    key === 'validated' ||
    key === 'delivered' ||
    key === 'verified'
  ) {
    return key;
  }
  return 'received';
}

/** Backend stage status is a free-form string today. We coerce to the UI enum
 *  based on the values the workflow service documents; unknowns fall back to
 *  "pending". `skipped` and `rejected` are terminal verdicts and must NOT
 *  collapse into "pending" — a completed run whose feed was never pushed
 *  reports Delivered as `skipped`, and rendering that as pending would claim a
 *  delivery is still coming. */
function toPipelineStageState(status: string): BatchPipelineStage['state'] {
  const s = status.trim().toLowerCase();
  if (s === 'completed' || s === 'complete' || s === 'done') return 'complete';
  if (s === 'in_progress' || s === 'in-progress' || s === 'active') return 'active';
  if (s === 'failed' || s === 'error' || s === 'errored') return 'failed';
  if (s === 'skipped' || s === 'not_run' || s === 'not-run') return 'skipped';
  if (s === 'rejected' || s === 'declined') return 'rejected';
  return 'pending';
}

/** Title-cases a single backend status token (e.g. "scheduled" → "Scheduled")
 *  for display inside the pending-state pill. */
function titleCase(s: string): string {
  const trimmed = s.trim();
  if (trimmed.length === 0) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function toPipelineStage(wire: BatchStageWire): BatchPipelineStage {
  const key = toPipelineStageKey(wire.name);
  const state = toPipelineStageState(wire.status);
  const pendingLabel = state === 'pending' ? titleCase(wire.status) : undefined;
  return {
    key,
    label: wire.name,
    // Null is preserved, not coerced to 0 — the strip renders "unknown" and
    // "genuinely none" differently.
    count: wire.count,
    state,
    suppressed: wire.suppressed === true,
    ...(pendingLabel ? { pendingLabel } : {}),
    // Carried so the coverage phase can measure enrichment duration off the
    // Enriched stage. Spread conditionally per `exactOptionalPropertyTypes`.
    ...(wire.startedAt != null ? { startedAt: wire.startedAt } : {}),
    ...(wire.completedAt != null ? { completedAt: wire.completedAt } : {}),
  };
}

/** Maps a live-stage name to the fixed 5-verb pipeline key set, tolerating both
 *  the workflow's SCREAMING stage vocabulary (`ENRICHMENT`, `DELIVERY`, …) and
 *  the retrospective verb names (`Enriched`, `Delivered`, …). Returns `null` for
 *  stages with no timeline counterpart (e.g. `APPROVAL`, `COMPLETED`) so the
 *  live merge leaves those header rows untouched rather than mis-highlighting. */
/** Pipeline states past the approval gate — the approval endpoint 409s on all of them, so a
 *  batch in one of these never counts as awaiting approval (see `isAwaitingApproval`). */
const SETTLED_PIPELINE_STATUSES: ReadonlySet<string> = new Set(['COMPLETED', 'FAILED', 'REJECTED']);

const LIVE_STAGE_KEY: Record<string, BatchPipelineStageKey> = {
  import: 'received',
  received: 'received',
  curation: 'curated',
  curated: 'curated',
  enrichment: 'enriched',
  enriched: 'enriched',
  distribution: 'validated',
  validation: 'validated',
  validated: 'validated',
  delivery: 'delivered',
  delivered: 'delivered',
  verification: 'verified',
  verified: 'verified',
};
function liveStageToKey(name: string): BatchPipelineStageKey | null {
  return LIVE_STAGE_KEY[name.trim().toLowerCase()] ?? null;
}

/** Translates a 0–100 coverage percent into the green/orange/red tone the UI
 *  consumes. Thresholds intentionally lenient — backend has no dedicated tone
 *  field so the FE owns the visual encoding. */
function toCoverageTone(percent: number): CoverageTone {
  if (percent >= 95) return 'green';
  if (percent >= 90) return 'orange';
  return 'red';
}

function toCategoryCoverageRow(wire: CategoryCoverageWire): CategoryCoverageRow {
  const percent = nz(wire.percent);
  return { label: wire.name, percent, tone: toCoverageTone(percent) };
}

/** Builds an attribute coverage row. `total` is the batch-level enriched count
 *  (passed in) — wire has only the per-attribute `count`, so the parent supplies
 *  the denominator so rows render as e.g. `96/100`, not `96/96`. */
function toAttributeCoverageRow(wire: AttributeCoverageWire, total: number): AttributeCoverageRow {
  const percent = nz(wire.percent);
  const count = nz(wire.count);
  return {
    label: wire.name,
    current: count,
    total,
    percent,
    tone: toCoverageTone(percent),
  };
}

/** Three-column "Batch Processing Details" panel — derived from the flat
 *  `deliveryHealth` block. `isDeliveryLocked` is passed in (it depends on the
 *  header's trial + awaiting-approval flags, which don't live on the coverage
 *  slice). Every numeric is null-coerced so a half-populated payload renders. */
function buildProcessingColumns(
  dh: BatchDeliveryHealthWire | null,
  isDeliveryLocked: boolean,
): BatchProcessingColumn[] {
  const productsInBatch = nz(dh?.productsInBatch);
  const newProducts = nz(dh?.newProducts);
  const productsEnriched = nz(dh?.productsEnriched);
  const averageQualityScore = nz(dh?.averageQualityScore);
  const newlyEnrichedProducts = nz(dh?.newlyEnrichedProducts);
  const needsAttention = nz(dh?.needsAttention);
  // Trial batches paused at AWAITING_APPROVAL never reach a real delivery —
  // the Delivered column's drill-in stats stay locked behind the premium badge.
  return [
    {
      title: 'Received',
      rows: [
        { label: 'Products in Batch', value: productsInBatch.toLocaleString() },
        { label: 'New Products', value: newProducts.toLocaleString() },
      ],
    },
    {
      title: 'Enriched',
      rows: [
        // Not a drill-in per design — only the Delivered column's "Newly
        // Enriched Products" / "Needs Attention" rows navigate to the products page.
        { label: 'Products Enriched', value: productsEnriched.toLocaleString() },
        { label: 'Average Quality Score', value: `${(averageQualityScore * 10).toFixed(2)}/100` },
      ],
    },
    {
      title: 'Delivered',
      rows: [
        {
          label: 'Newly Enriched Products',
          value: newlyEnrichedProducts.toLocaleString(),
          drillIn: true,
          disabled: newlyEnrichedProducts === 0 || isDeliveryLocked,
          locked: isDeliveryLocked,
        },
        {
          label: 'Needs Attention',
          value: isDeliveryLocked ? 'NA' : needsAttention.toLocaleString(),
          drillIn: true,
          disabled: needsAttention === 0 || isDeliveryLocked,
          locked: isDeliveryLocked,
          ...(isDeliveryLocked
            ? { lockedTooltip: 'Needs attention is available in production.' }
            : {}),
        },
      ],
    },
  ];
}

/** Backend doesn't yet break Needs Attention into named buckets at the batch
 *  level; we surface a single-bucket placeholder so the donut renders. */
function buildAttention(dh: BatchDeliveryHealthWire | null): {
  total: number;
  items: BatchAttentionItem[];
} {
  const total = nz(dh?.needsAttention);
  const items: BatchAttentionItem[] =
    total > 0
      ? [{ label: 'Needs Attention', count: total, color: 'indigo-dark' as AttentionColor }]
      : [];
  return { total, items };
}

/** Converts a keyed-map coverage object (occasion/tag/trend shape) into a
 *  sorted BatchAttributeRow array, descending by count. */
function coverageMapToRows(
  map: Record<string, { count: number; percentage: number }> | null | undefined,
): BatchAttributeRow[] {
  if (!map) return [];
  return Object.entries(map)
    .map(([label, entry]) => ({ label, count: entry.count }))
    .sort((a, b) => b.count - a.count);
}

/** Top Enriched Attribute Values → backed by `tagCoverage`.
 *  Occasions → backed by `occasionCoverage` (value.percentage used as `count`
 *  because BatchAttributeList renders it via `formatValue` as a percentage).
 *  Keywords → no API field available yet (TODO: wire once backend provides it).
 *  Trends → backed by `trendCoverage`. */
function buildAttributesAndTrends(wire: EnrichmentJobCoverageResponse): {
  attributes: BatchAttributeRow[];
  trends: BatchAttributeRow[];
  occasions: BatchAttributeRow[];
  keywords: BatchAttributeRow[];
} {
  const occasions: BatchAttributeRow[] = wire.occasionCoverage
    ? Object.entries(wire.occasionCoverage).map(([name, entry]) => ({
        label: name,
        count: entry.percentage,
      }))
    : [];
  return {
    attributes: coverageMapToRows(wire.tagCoverage),
    trends: coverageMapToRows(wire.trendCoverage),
    occasions,
    // TODO: wire to the correct API field once backend exposes keyword data.
    keywords: [],
  };
}

/** Coverage stats row — flatten the wire fields into the 4 cards the UI shows.
 *  `status` and `enrichedStage` are passed in (both are header-derived): the
 *  duration is measured off the Enriched stage's own timestamps, which live on
 *  the header timeline, not the coverage payload. */
function buildEnrichmentCoverage(
  ec: BatchEnrichmentCoverageWire | null,
  status: BatchStatus,
  enrichedStage: Pick<BatchPipelineStage, 'startedAt' | 'completedAt'> | null,
): EnrichmentCoverage {
  const skusInBatch = nz(ec?.skusInBatch);
  const enriched = nz(ec?.enriched);
  const coveragePercent = nz(ec?.coveragePercent);
  const newProducts = nz(ec?.newProducts);

  // Enrichment duration is measured on the Enriched pipeline stage alone — from
  // that stage's own start to its completion — not from batch creation, which
  // conflated queue/curation time with actual enrichment time.
  const enrichedStartedAt = enrichedStage?.startedAt ?? null;
  const enrichedCompletedAt = enrichedStage?.completedAt ?? null;
  // Failed/cancelled batches never reach a real completion — a batch that
  // terminally failed mid-enrichment must not show a forever-growing "running"
  // timer, so it surfaces "NA" instead.
  const isTerminalFailure = status === 'failed' || status === 'cancelled';
  // Genuinely running: the stage has started, hasn't completed, and the batch
  // hasn't terminally failed. Drives the elapsed-to-now value + active label.
  const isEnriching = !!enrichedStartedAt && !enrichedCompletedAt && !isTerminalFailure;

  // Three states, keyed off the Enriched stage lifecycle:
  //  • not started        → "Not started"
  //  • completed          → fixed startedAt→completedAt span
  //  • running            → elapsed startedAt→now (grows until it completes)
  //  • terminally failed  → "NA" (see above)
  let enrichmentDurationValue: string;
  let enrichmentDurationSub: CoverageStat['sub'];
  if (!enrichedStartedAt) {
    enrichmentDurationValue = 'Not started';
    enrichmentDurationSub = { text: '', tone: 'neutral' };
  } else if (enrichedCompletedAt) {
    enrichmentDurationValue = formatProcessingDuration(enrichedStartedAt, enrichedCompletedAt);
    enrichmentDurationSub = {
      text: `${formatDateTimeShort(enrichedStartedAt)} → ${formatDateTimeShort(enrichedCompletedAt)}`,
      tone: 'neutral',
    };
  } else if (isTerminalFailure) {
    enrichmentDurationValue = 'NA';
    enrichmentDurationSub = { text: '', tone: 'neutral' };
  } else {
    enrichmentDurationValue = formatProcessingDuration(enrichedStartedAt, null);
    enrichmentDurationSub = {
      text: `Running since ${formatDateTimeShort(enrichedStartedAt)}`,
      tone: 'neutral',
    };
  }

  const stats: CoverageStat[] = [
    {
      label: 'Products in This Batch',
      value: skusInBatch.toLocaleString(),
      tooltip: 'Total products received for enrichment in this batch run.',
    },
    {
      label: 'Products Enriched',
      value: enriched.toLocaleString(),
      tooltip: 'Products that received at least one Lily enrichment update.',
      sub: {
        text: `${String(Math.round(coveragePercent))}% coverage`,
        tone: 'positive',
      },
    },
    {
      label: 'First Time Enrichments',
      value: newProducts.toLocaleString(),
      tooltip: 'Products enriched by Lily for the first time (vs. updated from a prior run).',
      sub: { text: 'All enriched', tone: 'positive' },
    },
    {
      label: 'Enrichment duration',
      value: enrichmentDurationValue,
      sub: enrichmentDurationSub,
      isInProgress: isEnriching,
      tooltip:
        'Time spent in the enrichment stage, from when enrichment started to when it completed.',
    },
  ];
  return {
    stats,
    categories: (ec?.categoryLevel ?? []).map(toCategoryCoverageRow),
    attributes: (ec?.attributeLevel ?? []).map((a) => toAttributeCoverageRow(a, enriched)),
  };
}

/** Joins a before/after score pair into display rows, keyed by the caller's
 *  label accessor. The two sides are separate arrays on the wire, and the agent
 *  grades a WIDER set at PRE than at POST (POST only re-grades what enrichment
 *  touched), so the row set is the UNION of both — iterating only the after side
 *  silently drops every attribute that was scored before enrichment and not
 *  after. A side a row is missing from stays `null` ("never graded"), which the
 *  UI renders as an empty state rather than a zero bar; an explicit 0.0 on the
 *  wire is a real score and is preserved as 0. */
function buildQualityScoreRows<T extends { score: number | null }>(
  after: T[] | null | undefined,
  before: T[] | null | undefined,
  labelOf: (row: T) => string,
): QualityScoreRow[] {
  const afterByLabel = new Map((after ?? []).map((r) => [labelOf(r), r.score]));
  const beforeByLabel = new Map((before ?? []).map((r) => [labelOf(r), r.score]));
  // Union, after-side order first so the common case keeps its existing ordering.
  const labels = [...new Set([...afterByLabel.keys(), ...beforeByLabel.keys()])];
  return (
    labels
      .map((label) => ({
        label,
        beforeScore: beforeByLabel.get(label) ?? null,
        score: afterByLabel.get(label) ?? null,
      }))
      // Descending by score for visual emphasis; rows with no after score sort
      // last, since there is no post-enrichment result to rank them by.
      .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
  );
}

/** Optimization Quality tab content — splits the flat scores into the two
 *  panels (Product Content / Category-Level) the UI renders. The backend ships
 *  before/after as separate arrays rather than inline on each score entry, so
 *  each panel's rows are joined by {@link buildQualityScoreRows}. Takes the `optimizationQuality`
 *  wire field directly so feed-audit can reuse it off its raw job detail (which
 *  is the same endpoint payload) without a second mapped fetch. */
export function buildOptimizationQuality(
  oq: BatchOptimizationQualityWire | null,
): OptimizationQuality {
  const panels: QualityScorePanel[] = [
    {
      title: 'Product Content',
      subtitle: 'Scored against best-practice guidelines',
      rows: buildQualityScoreRows(
        oq?.productContentScores,
        oq?.productContentScoresBefore,
        (r) => r.attribute,
      ),
    },
    {
      title: 'Category-Level',
      subtitle: 'Quality scores by product category',
      rows: buildQualityScoreRows(
        oq?.categoryLevelScores,
        oq?.categoryLevelScoresBefore,
        (r) => r.category,
      ),
    },
  ];
  return {
    sampleQualityScore: {
      beforeScore: nz(oq?.beforeEnrichmentScore),
      afterScore: nz(oq?.afterEnrichmentScore),
    },
    qualityScore: {
      title: 'Quality Score',
      subtitle: oq?.belowThresholdNote ?? 'Scored against platform best practices',
      panels,
    },
    dimensionScores: (oq?.dimensionScores ?? []).map((d) => ({
      dimension: d.dimension,
      before: nz(d.before),
      after: nz(d.after),
      lift: nz(d.lift),
    })),
    curatedPreScoredCount: oq?.curatedPreScoredCount ?? null,
  };
}

/** Builds the Performance Signals tab data from the batch detail wire payload.
 *  `popularTerms` is empty until the API exposes term-level data.
 *  Coverage maps are transformed via the shared utils in `~/utils/coverage-transforms`. */
/** Coerces wire KPIs to the domain shape, returning null unless the before/after
 *  pair is fully populated — the enrichments service can send a truthy-but-partial
 *  `performanceKpis` while backfill is in progress, which would otherwise crash the
 *  delta card on a missing nested field. */
function toPerformanceKpis(wire: PerformanceKpisWire | null): PerformanceKpis | null {
  if (!wire) return null;
  // Wire types declare the nested pair as required, but the service can omit
  // it mid-backfill — view it as possibly-undefined to guard honestly.
  // `keywordsMatched`, `monthlySearchVolume` and `highVolumeProducts` were
  // dropped from the payload by gap_analysis_v3, leaving only `occasionsCovered`.
  const pairs: (Partial<BeforeAfterCountWire> | undefined)[] = [wire.occasionsCovered];
  const complete = pairs.every((p) => typeof p?.before === 'number' && typeof p.after === 'number');
  return complete ? wire : null;
}

function buildPerformanceSignals(wire: EnrichmentJobCoverageResponse): PerformanceSignals {
  const { items: occasions, maxValue: occasionsMax } = occasionCoverageToComparison(
    wire.occasionCoverage,
    wire.occasionCoverageBefore,
  );
  const { items: trends, maxValue: trendsMax } = trendCoverageToComparison(
    wire.trendCoverage,
    wire.trendCoverageBefore,
  );
  const popularAttributes = tagCoverageToAttributes(wire.tagCoverage);

  return {
    popularTerms: [],
    popularAttributes,
    occasions,
    occasionsMax,
    trends,
    trendsMax,
    // Wire shape is structurally identical to the domain PerformanceKpis, but
    // may arrive partial during backfill — normalise to null unless complete.
    kpis: toPerformanceKpis(wire.performanceKpis),
  };
}

/** Assembles the run configuration the backend echoes on batch detail (same
 *  fields as the creation payload), or `undefined` when none is present so the
 *  delivery panel falls back to the full taxonomy rather than an empty picker. */
function toRunConfig(wire: EnrichmentJobHeaderResponse): EnrichmentRunConfig | undefined {
  // Prefer the nested `enrichmentConfig` (current shape); fall back to the legacy
  // flat top-level fields for older responses. The delivery picker offers what the
  // run enriched, so narrow off `enrichedAttributes` when present (the deliverable
  // set), else the requested `contentAttributes`.
  const ec = wire.enrichmentConfig;
  const contentAttributes = ec
    ? (ec.enrichedAttributes ?? ec.contentAttributes ?? null)
    : (wire.contentAttributes ?? null);
  const searchDescriptors = ec
    ? {
        enabled: ec.searchDescriptorsEnabled ?? false,
        phraseDescriptions: ec.phraseDescriptionsEnabled ?? false,
      }
    : (wire.searchDescriptors ?? null);
  const scoring = ec
    ? { preScoring: ec.scoringPreEnabled ?? false, postScoring: ec.scoringPostEnabled ?? false }
    : (wire.scoring ?? null);

  const config: EnrichmentRunConfig = {
    ...(contentAttributes && contentAttributes.length > 0 ? { contentAttributes } : {}),
    ...(searchDescriptors ? { searchDescriptors } : {}),
    ...(scoring ? { scoring } : {}),
    ...(ec?.retailer ? { retailer: ec.retailer } : {}),
    ...(ec?.targetLocales?.length ? { targetLocales: ec.targetLocales } : {}),
  };
  return Object.keys(config).length > 0 ? config : undefined;
}

/** Stored targeting filters → the Batch Info sheet's shape: strings and string arrays only.
 *  Numbers, booleans and nulls are dropped, as the scheduled-enrichment adapter does. */
export function toEnrichmentFilters(
  raw: Record<string, unknown> | null | undefined,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(raw ?? {})) {
    if (typeof value === 'string') out[key] = value;
    else if (Array.isArray(value)) {
      out[key] = value.filter((v): v is string => typeof v === 'string');
    }
  }
  return out;
}

/** Phase-1 mapper — `GET /jobs/{id}/header` → the shell slice. `deliveredAt` and
 *  other optionals are conditionally spread so absent fields stay `undefined`
 *  (required under `exactOptionalPropertyTypes`). Every nullable wire field is
 *  coerced to a sensible UI default rather than throwing — backfill is still in
 *  progress on the enrichments service. */
export function toBatchHeader(wire: EnrichmentJobHeaderResponse): BatchHeader {
  const platform = toPlatformName(wire.connectorCode);
  const status = resolveDisplayStatus(wire.status, wire.pipelineStatus);
  const rawStages = (wire.stages ?? []).map(toPipelineStage);
  // When the batch overall has failed, surface the failure on whichever
  // stage was mid-flight — the backend doesn't emit a `failed` status per
  // stage, so the active step is the natural place to render the error.
  const stages: BatchPipelineStage[] =
    status === 'failed'
      ? rawStages.map((s) => (s.state === 'active' ? { ...s, state: 'failed' } : s))
      : rawStages;
  const runConfig = toRunConfig(wire);
  return {
    batchId: wire.batchId ?? '—',
    name: wire.name ?? '',
    status,
    source: toUseCaseLabel(wire.useCase, platform),
    useCase: wire.useCase,
    triggerSource: wire.source ?? '',
    createdAt: wire.createdAt ?? '',
    // Audit stamps: conditionally spread so an absent wire field stays undefined
    // (required under exactOptionalPropertyTypes) and renders as an em-dash.
    ...(wire.createdBy != null ? { createdBy: wire.createdBy } : {}),
    ...(wire.lastActionBy != null ? { lastActionBy: wire.lastActionBy } : {}),
    ...(wire.lastActionAt != null ? { lastActionAt: wire.lastActionAt } : {}),
    ...(wire.lastActionType != null ? { lastActionType: wire.lastActionType } : {}),
    ...(wire.filters != null ? { filters: toEnrichmentFilters(wire.filters) } : {}),
    ...(wire.plannedCost != null ? { plannedCost: wire.plannedCost } : {}),
    // Cost gate: absent means nothing pending (a backend that predates it never sends one,
    // so null is right either way) — lets the estimate sync see the gate appear and clear.
    costGate: wire.costGate ?? null,
    // Batch pause / retry — conditionally spread so absent stays undefined.
    ...(wire.batchStatus != null ? { batchStatus: wire.batchStatus } : {}),
    ...(wire.retryAttempts != null ? { retryAttempts: wire.retryAttempts } : {}),
    ...(wire.activeRetryRunId != null ? { activeRetryRunId: wire.activeRetryRunId } : {}),
    ...(wire.pausedAt != null ? { pausedAt: wire.pausedAt } : {}),
    ...(wire.pausedBy != null ? { pausedBy: wire.pausedBy } : {}),
    ...(wire.productsExcluded != null ? { productsExcluded: wire.productsExcluded } : {}),
    // List Archive stamps — `null` (not absent) so consumers can read them
    // directly. History rides on detail payloads only (null on list rows).
    archivedAt: wire.archivedAt ?? null,
    archivedBy: wire.archivedBy ?? null,
    archiveHistory: wire.archiveHistory ?? null,
    // Fine-grained current stage + curated count — surfaced by the batch info
    // sheet; spread conditionally so an absent wire field stays `undefined`.
    ...(wire.currentStage ? { currentStage: wire.currentStage } : {}),
    ...(wire.productsCuratedTotal != null
      ? { productsCuratedTotal: wire.productsCuratedTotal }
      : {}),
    ...productClassesOf(wire.productClasses),
    ...(wire.completedAt ? { deliveredAt: wire.completedAt } : {}),
    sourcePlatform: platform,
    destinationPlatform: platform,
    stages,
    // A settled pipeline (COMPLETED/FAILED/REJECTED) has left the approval gate even if the
    // top-level status lagged behind — never re-offer an approval the endpoint would 409.
    isAwaitingApproval:
      wire.status === 'AWAITING_APPROVAL' &&
      !SETTLED_PIPELINE_STATUSES.has(wire.pipelineStatus ?? ''),
    // Decoupled-delivery sub-state. `isAwaitingApproval` stays true while
    // delivering, so these flags let the UI tell "awaiting operator" apart from
    // "delivering"/"rejected".
    isDelivering: wire.pipelineStatus === 'DELIVERING',
    isRejected: wire.pipelineStatus === 'REJECTED' || wire.status === 'REJECTED',
    ...(wire.pipelineStatus != null ? { pipelineStatus: wire.pipelineStatus } : {}),
    ...(wire.deliveryJobId != null ? { deliveryJobId: wire.deliveryJobId } : {}),
    // Search/phrase-descriptors toggles from the echoed run config — gate the
    // product page's Search Descriptors tab and the /coverage cards' rows for
    // those steps. `null` when absent (treated as "unknown", not disabled).
    searchDescriptorsEnabled: wire.enrichmentConfig?.searchDescriptorsEnabled ?? null,
    phraseDescriptionsEnabled: wire.enrichmentConfig?.phraseDescriptionsEnabled ?? null,
    // Delivery pipeline's own workflow id — the delivery-approval endpoint target.
    ...(wire.pipelineWorkflowId ? { pipelineWorkflowId: wire.pipelineWorkflowId } : {}),
    isTrial: wire.source === 'TRIAL',
    // Run configuration echoed by the backend, when present — spread so a batch
    // that predates the echo leaves `runConfig` absent rather than an empty object.
    ...(runConfig ? { runConfig } : {}),
    // Every language the run delivers. A backend that predates the echo sends none, and every
    // such batch was English-only, so `en-US` is the correct default rather than a guess.
    locales:
      wire.enrichmentConfig?.locales && wire.enrichmentConfig.locales.length > 0
        ? wire.enrichmentConfig.locales
        : ['en-US', ...(runConfig?.targetLocales ?? [])],
    // Always-on attributes (Meta only) — kept separate from `runConfig` rather
    // than folded into `EnrichmentRunConfig`, which has no honoured/always-on
    // concept and is consumed by several call sites that don't need this split.
    ...(wire.enrichmentConfig?.alwaysOnAttributes
      ? { alwaysOnAttributes: wire.enrichmentConfig.alwaysOnAttributes }
      : {}),
    ...(wire.curationRefId ? { curationRefId: wire.curationRefId } : {}),
    ...(wire.importRefId ? { importRefId: wire.importRefId } : {}),
    // Temporal slug for the CSV export's sourceWorkflowId (falls back to
    // batchId, which currently mirrors it on the wire).
    ...((wire.workflowId ?? wire.batchId)
      ? { workflowId: wire.workflowId ?? wire.batchId ?? '' }
      : {}),
    // Export reads enriched rows by workflow id. An experiment's `workflowId` can be a
    // delivery-only retry with no enrichment rows, so prefer the run that enriched.
    ...((wire.enrichmentWorkflowId ?? wire.workflowId ?? wire.batchId)
      ? { exportWorkflowId: wire.enrichmentWorkflowId ?? wire.workflowId ?? wire.batchId ?? '' }
      : {}),
    // Emit the curation-API form (`GOOGLE_MERCHANT`, …) so consumers can
    // pass `batch.connectorCode` straight into curation requests without a
    // call-site translation step.
    ...(() => {
      const curationCode = toCurationConnectorCode(wire.connectorCode);
      return curationCode ? { connectorCode: curationCode } : {};
    })(),
  };
}

/** Phase-3 mapper — `GET /jobs/{id}/coverage` → the Delivery Health + Enrichment
 *  Coverage slice. Takes the resolved {@link BatchHeader} because a few
 *  derivations (delivery lock, enrichment duration) depend on header fields the
 *  coverage payload doesn't carry. `productsEnriched` is passed through raw (not
 *  `nz`-coerced) so a missing block stays absent — the products page treats
 *  `undefined` as "no figure to compare against" rather than as a real zero. */
export function toBatchCoverageDetail(
  wire: EnrichmentJobCoverageResponse,
  header: BatchHeader,
): BatchCoverageDetail {
  const { attributes, trends, occasions, keywords } = buildAttributesAndTrends(wire);
  // Trial batches paused at AWAITING_APPROVAL never reach a real delivery.
  const isDeliveryLocked = header.isTrial && header.isAwaitingApproval;
  const enrichedStage = header.stages.find((s) => s.key === 'enriched') ?? null;
  return {
    processing: buildProcessingColumns(wire.deliveryHealth, isDeliveryLocked),
    attention: buildAttention(wire.deliveryHealth),
    attributes,
    trends,
    occasions,
    keywords,
    enrichmentCoverage: buildEnrichmentCoverage(
      wire.enrichmentCoverage,
      header.status,
      enrichedStage,
    ),
    performanceSignals: buildPerformanceSignals(wire),
    ...(wire.deliveryHealth?.productsEnriched != null
      ? { productsEnriched: wire.deliveryHealth.productsEnriched }
      : {}),
  };
}

/** Phase-2 mapper — `GET /jobs/{id}/live-stage` → refined pipeline stages.
 *  Stages whose live name has no 5-verb counterpart (`APPROVAL`, `COMPLETED`)
 *  are dropped so the page's merge leaves those header rows untouched. */
export function toBatchLiveStage(wire: LiveStageResponse): BatchLiveStage {
  const stages: BatchPipelineStage[] = (wire.stageStatuses ?? []).flatMap((s) => {
    const key = liveStageToKey(s.stage);
    if (!key) return [];
    const state = toPipelineStageState(s.status);
    const pendingLabel = state === 'pending' ? titleCase(s.status) : undefined;
    // Prefer the succeeded count; fall back to expected so a running stage still
    // shows a number. `null` on both means "not tracked" — kept as null so the
    // strip renders an em dash rather than claiming a count of 0.
    const count = s.succeeded ?? s.expected ?? null;
    const stage: BatchPipelineStage = {
      key,
      label: s.stage,
      count,
      state,
      suppressed: false,
      ...(pendingLabel ? { pendingLabel } : {}),
      ...(s.enteredAt != null ? { startedAt: s.enteredAt } : {}),
      ...(s.exitedAt != null ? { completedAt: s.exitedAt } : {}),
    };
    return [stage];
  });
  return {
    currentStage: wire.currentStage ?? null,
    stages,
    costGate: wire.costGate ?? null,
  };
}

/** Overlays live per-stage counters onto the retrospective header timeline,
 *  matching by stage key. Header stages with no live counterpart are kept as-is;
 *  the 5-verb header set stays authoritative for which stages exist. Returns the
 *  header stages unchanged when there's no live data (`204` → null). */
export function mergeLiveStages(
  headerStages: BatchPipelineStage[],
  live: BatchLiveStage | null | undefined,
): BatchPipelineStage[] {
  if (!live) return headerStages;
  const liveByKey = new Map(live.stages.map((s) => [s.key, s]));
  return headerStages.map((hs) => {
    const ls = liveByKey.get(hs.key);
    return ls ? { ...hs, count: ls.count, state: ls.state } : hs;
  });
}

/** Top-level mapper for the legacy single-call aggregate (`GET /jobs/{id}`).
 *  Composed from the phase mappers so the aggregate can never drift from the
 *  split. Still used by the products page, feed-audit debug, and trial. */
export function toBatchDetail(wire: BatchDetailResponse): BatchDetail {
  const header = toBatchHeader(wire);
  return {
    ...header,
    ...toBatchCoverageDetail(wire, header),
    optimizationQuality: buildOptimizationQuality(wire.optimizationQuality),
  };
}

// ---------------------------------------------------------------------------
// Product enrichment — GET /products/{productId}
// ---------------------------------------------------------------------------

/** Maps an API field name (snake_case) to the renderer strategy. Field-name
 *  driven because the wire payload doesn't carry an explicit content type. */
const FIELD_CONTENT_TYPE: Record<string, OptimizationContentType> = {
  title: 'plain',
  product_highlight: 'bullets',
  product_detail: 'product-details',
  product_type: 'tags',
  structured_title: 'structured-json',
  structured_description: 'structured-json',
  product_qa: 'qa',
  // Same `[{q, a}, …]` shape as product_qa — the agent catalog's own Q&A field.
  // Without this it fell through to `text`, which both rendered the raw JSON as
  // a blob and offered an editor the API rejects (array-of-objects values are
  // lossy to rebuild from the flattened display string, so the service refuses
  // them). Mapping it here fixes the rendering and the gating together.
  question_and_answer: 'qa',
};

/** Custom display labels for known API field names — overrides the generic
 *  Title-Case fallback so the UI matches the product copy in the designs
 *  (e.g. plural "Highlights"). */
const FIELD_LABEL: Record<string, string> = {
  title: 'Product Title',
  description: 'Product Description',
  product_highlight: 'Product Highlights',
  product_detail: 'Product Details',
  product_type: 'Product Type',
  item_group_id: 'Item Group ID',
  retailer_id: 'Retailer ID',
};

/** Meta enrichment reads name fields in camelCase (`itemGroupId`) while GMC and
 *  curation use snake_case; folding to snake_case lets one label/content-type/
 *  "Current" lookup serve both. Already-snake names pass through unchanged. */
function toSnakeFieldKey(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

/** Converts a snake_case API field name to a Title-Case display label,
 *  e.g. `'product_highlight'` → `'Product Highlight'`. Used as the fallback
 *  for field names that aren't in the `FIELD_LABEL` overrides map. */
function formatFieldLabel(name: string): string {
  return name
    .split('_')
    .map((part) => (part.length > 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/** Normalises the `product_detail` field value across two historical wire shapes.
 *
 * - **Old format** `[{ "value": "SectionName: AttrName: Val | ..." }]` — unwraps
 *   to a plain pipe-delimited string so the legacy page parser handles it.
 * - **New format** `[{ "section_name": ..., "attribute_name": ..., "attribute_value": ... }]`
 *   — passed through unchanged as a JSON string; the page parser detects snake_case
 *   keys and maps them directly to `ProductDetailAttribute`.
 * - All other fields, or if JSON parsing fails, are returned as-is. */
function unwrapJsonValueField(name: string, raw: string): string {
  if (name !== 'product_detail') return raw;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return raw;
    const first = parsed[0] as Record<string, unknown>;
    // Old format: array of { value } wrappers — flatten to pipe-delimited string.
    if ('value' in first) {
      return (parsed as { value: string }[]).map((p) => p.value).join(' | ');
    }
    // New format: structured attribute objects — keep raw JSON for page parser.
    if ('section_name' in first || 'sectionName' in first) {
      return raw;
    }
  } catch {
    // not JSON-wrapped — return as-is
  }
  return raw;
}

/** Translates a single optimized field from the enrichment endpoint into the
 *  UI row shape. The wire payload only carries the optimized output plus an
 *  `impact` severity label — there is no "current" content here, so the row
 *  renders one panel and an impact badge instead of a before/after grid. */
function toProductOptimization(field: ProductEnrichmentFieldWire): ProductOptimization {
  // `field` keeps the wire name (attribute PATCH + edit provenance key on it);
  // only the display lookups use the folded snake_case key.
  const key = toSnakeFieldKey(field.name);
  return {
    field: field.name,
    label: FIELD_LABEL[key] ?? formatFieldLabel(key),
    impact: field.impact ?? 'Optimized',
    contentType: FIELD_CONTENT_TYPE[key] ?? 'text',
    // Highlights go through the shared formatter so the bullets renderer gets the
    // canonical pipe-delimited value on every surface.
    value:
      field.name === 'product_highlight'
        ? formatProductHighlights(field.value)
        : unwrapJsonValueField(field.name, field.value),
  };
}

/** Meta category attributes (`shoe_type`, `pant_fit`, …) as read-only Enriched
 *  Attributes rows. They live in their own column, not `fields[]`, so a key the
 *  fields already carry is skipped rather than shown twice. */
function toCategoryAttributeOptimizations(wire: ProductEnrichmentResponse): ProductOptimization[] {
  // Snake-folded so a camelCase Meta field (`sizeSystem`) still suppresses its twin.
  const fieldNames = new Set(wire.fields.map((f) => toSnakeFieldKey(f.name)));
  return metaCategoryAttributeEntries(wire.categoryAttributes)
    .filter((entry) => !fieldNames.has(entry.key))
    .map((entry) => ({
      field: entry.key,
      label: entry.label,
      impact: 'Optimized',
      contentType: 'text',
      value: entry.value,
      // workflow-service has no attribute PATCH for category_attributes: saving one 400s.
      readOnly: true,
    }));
}

/** Meta's `category` sits beside `fields[]` on the wire, not in it, so it never
 *  reached the page. Shown read-only: it is the merchant's value, with no PATCH. */
function toMetaCategoryOptimization(wire: ProductEnrichmentResponse): ProductOptimization[] {
  const category = wire.category?.trim();
  if (wire.useCase !== 'META_ADS' || !category) return [];
  if (wire.fields.some((f) => toSnakeFieldKey(f.name) === 'category')) return [];
  return [
    {
      field: 'category',
      label: 'Category',
      impact: 'Optimized',
      contentType: 'text',
      value: category,
      readOnly: true,
    },
  ];
}

/** Enrichment field names use singular form (`product_highlight`) while the
 *  curation product detail response uses plural (`product_highlights`). Maps
 *  the enrichment-side name (folded to snake_case) to the curation-side key when
 *  they diverge; any field not listed here is looked up by its own name. */
const ENRICHMENT_TO_CURATION_FIELD: Record<string, string> = {
  product_highlight: 'product_highlights',
  product_detail: 'product_details',
  product_type: 'product_types',
  additional_image_link: 'additional_image_urls',
  image_link: 'image_url',
};

/** Attaches the `currentValue` ("Current" panel content) to each optimization
 *  by looking up the corresponding curation field in `rawFields`, applying the
 *  enrichment→curation field-name alias where needed. Returns the original
 *  array if `rawFields` is absent so callers don't need to null-check. */
export function applyCurrentValues(
  optimizations: ProductOptimization[],
  rawFields: Record<string, string> | undefined,
): ProductOptimization[] {
  if (!rawFields) return optimizations;
  return optimizations.map((opt) => {
    const key = toSnakeFieldKey(opt.field);
    const curationKey = ENRICHMENT_TO_CURATION_FIELD[key] ?? key;
    const currentValue = rawFields[curationKey];
    return currentValue ? { ...opt, currentValue } : opt;
  });
}

/** Maps one wire gap attribute row, renaming the terse wire fields to the
 *  domain's spelled-out ones. `netNewEncoded` stays tri-state — null on
 *  baseline rows is meaningful, so it must not collapse to false. */
function toGapAttribute(wire: GapAttributeWire): GapAttribute {
  return {
    tag: wire.tag,
    source: wire.source,
    isNetNew: wire.netNewEncoded,
    searchVolume: wire.searchVol,
    competitionLevel: wire.competition,
    matchedTerm: wire.matchedTerm,
  };
}

/** Maps one wire section bucket to its domain type. */
function toGapAnalysisSection(wire: GapAnalysisSectionWire): GapAnalysisSection {
  return {
    section: wire.section,
    rows: wire.rows.map(toGapAttribute),
  };
}

/** Maps the per-product summary. Field-for-field — the service returns camelCase
 *  (workflow-service PR #570 gave the read surface its own outbound DTO instead of
 *  reusing the agent's snake_case inbound one). */
function toGapAnalysisSummary(wire: GapAnalysisSummaryWire): GapAnalysisSummary {
  return {
    baselineAttributeCount: wire.baselineAttributeCount,
    enrichedAttributeCount: wire.enrichedAttributeCount,
    netNewCount: wire.netNewCount,
    restatedCount: wire.restatedCount,
    netNewSearchVolume: wire.netNewSearchVolume,
    similarityThreshold: wire.similarityThreshold,
    volumeStatus: wire.volumeStatus,
    issues: wire.issues,
  };
}

/** Maps the gap-analysis endpoint's response into the domain shape. Both the
 *  empty case (`summary: null`, `sections: []`) and a present summary with no
 *  sections are legitimate answers, so neither is normalised away. */
export function toGapAnalysisProduct(wire: GapAnalysisProductWire): GapAnalysisProduct {
  return {
    productId: wire.productId,
    status: wire.status,
    error: wire.error,
    summary: wire.summary ? toGapAnalysisSummary(wire.summary) : null,
    sections: wire.sections.map(toGapAnalysisSection),
  };
}

// ---------------------------------------------------------------------------
// Search descriptors + phrase descriptions — GET /products/{id}/search-descriptors
// ---------------------------------------------------------------------------

/** Maps one pivot and its phrases. Array order is the server's rank order and is
 *  preserved deliberately — the contract states both levels arrive pre-sorted, so
 *  re-sorting here would discard the ranking the agent produced. */
function toSearchPivot(wire: SearchPivotWire): SearchPivot {
  return {
    pivotPhrase: wire.pivotPhrase,
    pivotRank: wire.pivotRank,
    searchVolume: wire.searchVolume,
    associatedPhrases: (wire.associatedPhrases ?? []).map((phrase) => ({
      phrase: phrase.phrase,
      phraseRank: phrase.phraseRank,
      searchVolume: phrase.searchVolume,
    })),
  };
}

/** Maps the phrase-description half. Levels pass through as-is including nulls: a
 *  missing level is a real per-level outcome, not something to default. */
function toPhraseDescription(wire: PhraseDescriptionWire): PhraseDescription {
  return {
    status: wire.status,
    inputDescription: wire.inputDescription,
    inputDescriptionSource: wire.inputDescriptionSource,
    level1Description: wire.level1Description,
    level2Description: wire.level2Description,
    level2Base: wire.level2Base,
    level3Description: wire.level3Description,
    level3Error: wire.level3Error,
    levelsProduced: wire.levelsProduced,
    phrasesUsed: wire.phrasesUsed,
    generatedAt: wire.generatedAt,
  };
}

/** Maps the combined search-descriptor / phrase-description read. Thin by design —
 *  the endpoint already answers camelCase. The one normalisation that matters is
 *  `searchPhrases` defaulting to `[]`: the contract promises an array, and a null
 *  slipping through would break every consumer that maps over it. `phraseDescription`
 *  is left nullable because null carries meaning ("that strategy never ran"). */
export function toProductSearchDescriptors(
  wire: ProductSearchDescriptorsWire,
): ProductSearchDescriptors {
  return {
    productId: wire.productId,
    workflowId: wire.workflowId,
    phase: wire.phase,
    status: wire.status,
    keywordSource: wire.keywordSource,
    pivotCount: wire.pivotCount,
    phraseCount: wire.phraseCount,
    accuracy: wire.accuracy
      ? { score: wire.accuracy.score, reasoning: wire.accuracy.reasoning }
      : null,
    searchPhrases: (wire.searchPhrases ?? []).map(toSearchPivot),
    phraseDescription: wire.phraseDescription ? toPhraseDescription(wire.phraseDescription) : null,
    generatedAt: wire.generatedAt,
  };
}

/** Maps the enrichment response into the `ProductDetail` slices it populates.
 *  Returned partial is merged into the overview-detail object on the page.
 *  `enrichmentScore` is omitted when the API returns `score`/`originalScore`
 *  as `null` (e.g. Meta Ads enrichments that don't compute a score).
 *  `attributeCount` and `highlightCount` are derived from the enrichment
 *  `fields` array and override the curation-API values on merge. */
export function toProductEnrichment(
  wire: ProductEnrichmentResponse,
): Pick<
  ProductDetail,
  | 'enrichmentScore'
  | 'optimizations'
  | 'attributeCount'
  | 'highlightCount'
  | 'searchDescriptorCount'
  | 'externalId'
  | 'workflowId'
  | 'enrichedAt'
  | 'inBatch'
  | 'retailerPrompt'
  | 'sourceShipping'
> {
  const enrichmentScore =
    wire.score != null && wire.originalScore != null
      ? (() => {
          const before = nz(wire.originalScore);
          const after = nz(wire.score);
          return { before, after, delta: after - before };
        })()
      : undefined;

  const highlightField = wire.fields.find((f) => f.name === 'product_highlight');
  const highlightCount = highlightField?.value
    ? highlightField.value.split('|').filter(Boolean).length
    : 0;

  // Count product_detail entries across both wire shapes:
  // - New format: JSON array of attribute objects → count elements directly.
  // - Old/legacy format: pipe-delimited string (after unwrapping) → count segments.
  const productDetailField = wire.fields.find((f) => f.name === 'product_detail');
  const searchDescriptorCount = (() => {
    if (!productDetailField?.value) return 0;
    try {
      const parsed = JSON.parse(productDetailField.value) as unknown;
      if (Array.isArray(parsed)) return parsed.length;
    } catch {
      // not JSON — fall through to pipe-delimited count
    }
    return unwrapJsonValueField('product_detail', productDetailField.value)
      .split('|')
      .filter(Boolean).length;
  })();
  // Opt-in merchant-feed shipping snapshot the run saw; the page prefers the
  // live curation value and only falls back to this one.
  const sourceShipping = toSourceShippingAttributes(wire.sourceShippingAttributes, 'camel');

  return {
    ...(enrichmentScore ? { enrichmentScore } : {}),
    ...(sourceShipping ? { sourceShipping } : {}),
    externalId: wire.externalId,
    optimizations: [
      ...wire.fields
        .filter((f) => f.value && f.value !== '' && f.value.length > 0 && f.value !== '[]')
        .map(toProductOptimization),
      ...toCategoryAttributeOptimizations(wire),
      ...toMetaCategoryOptimization(wire),
    ].sort((a, b) => a.label.localeCompare(b.label)),
    attributeCount: wire.fields.length,
    highlightCount,
    searchDescriptorCount,
    enrichedAt: wire.createdAt,
    ...(wire.inBatch !== undefined ? { inBatch: wire.inBatch } : {}),
    // The receipt is optional: rows from runs with no retailer (and every older row) carry none.
    ...(wire.metadata.retailerPrompt?.retailer
      ? {
          retailerPrompt: {
            retailer: wire.metadata.retailerPrompt.retailer,
            bucket: wire.metadata.retailerPrompt.bucket ?? '',
            resolvedPromptPath: wire.metadata.retailerPrompt.resolvedPromptPath ?? '',
            fellBack: wire.metadata.retailerPrompt.fellBack === true,
          },
        }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// On-demand data-scoring — POST/GET /{workflowId}/products/{productId}/score
// ---------------------------------------------------------------------------

/** Drops the wire's null fields so the domain side carries only present values,
 *  keeping `undefined` (not `null`) to satisfy the project's exactOptional types. */
function toOnDemandScoreSide(wire: OnDemandScoreSideWire): OnDemandScoreSide {
  return {
    overallScore: wire.overallScore,
    ...(wire.dataLabel != null ? { dataLabel: wire.dataLabel } : {}),
    ...(wire.correctness != null ? { correctness: wire.correctness } : {}),
    ...(wire.completeness != null ? { completeness: wire.completeness } : {}),
    ...(wire.compliance != null ? { compliance: wire.compliance } : {}),
    ...(wire.relevance != null ? { relevance: wire.relevance } : {}),
    ...(wire.differentiation != null ? { differentiation: wire.differentiation } : {}),
    ...(wire.titleDeliveryCheck != null ? { titleDeliveryCheck: wire.titleDeliveryCheck } : {}),
    ...(wire.descriptionDeliveryCheck != null
      ? { descriptionDeliveryCheck: wire.descriptionDeliveryCheck }
      : {}),
    ...(wire.routeToReEnrichment != null ? { routeToReEnrichment: wire.routeToReEnrichment } : {}),
  };
}

/** Maps the on-demand score response (shared by POST and GET) into the domain
 *  result. The opaque debug `workflowId` on the wire is intentionally dropped —
 *  callers poll with the path workflowId, never this one. */
export function toOnDemandScoreResult(wire: OnDemandScoreResponseWire): OnDemandScoreResult {
  return {
    productId: wire.productId,
    ...(wire.externalId != null ? { externalId: wire.externalId } : {}),
    status: wire.status,
    reScored: wire.reScored ?? false,
    cacheBypassed: wire.cacheBypassed ?? false,
    ...(wire.scoreDelta != null ? { scoreDelta: wire.scoreDelta } : {}),
    ...(wire.scoredAt != null ? { scoredAt: wire.scoredAt } : {}),
    ...(wire.pre != null ? { pre: toOnDemandScoreSide(wire.pre) } : {}),
    ...(wire.post != null ? { post: toOnDemandScoreSide(wire.post) } : {}),
  };
}

// ---------------------------------------------------------------------------
// Attribute edit provenance — GET /products/{productId}/attribute-edits
// ---------------------------------------------------------------------------

/** Passes an edit row through unchanged apart from the type — the wire is
 *  already camelCase and the values are deliberately kept as raw JSON, since
 *  rendering them means reading the attribute's own shape, not a string. */
export function toProductAttributeEdit(wire: ProductAttributeEditWire): ProductAttributeEdit {
  return {
    field: wire.field,
    previousValue: wire.previousValue,
    newValue: wire.newValue,
    editedBy: wire.editedBy,
    editedAt: wire.editedAt,
    workflowId: wire.workflowId,
  };
}

/** The batch's per-class counts (`productClasses`): what was offered and what curation took in. */
function productClassesOf(raw: Record<string, unknown> | null | undefined): {
  productClassesOffered?: Record<string, unknown>;
  productClassesIncluded?: Record<string, number>;
} {
  if (!raw) return {};
  const offered = isRecord(raw.atCreation) ? raw.atCreation : undefined;
  let included: Record<string, number> | undefined;
  if (isRecord(raw.curated)) {
    included = {};
    for (const [cls, counts] of Object.entries(raw.curated)) {
      const n = isRecord(counts) ? counts.included : undefined;
      if (typeof n === 'number') included[cls] = n;
    }
  }
  return {
    ...(offered ? { productClassesOffered: offered } : {}),
    ...(included ? { productClassesIncluded: included } : {}),
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
